import { parsePhoneNumberFromString } from "libphonenumber-js/min";

/**
 * Canonicalization at the credential boundary.
 *
 * Better Auth's `phoneNumberValidator` is a boolean predicate: it accepts or
 * rejects, and then stores whatever string the client submitted. The database's
 * uniqueness constraint on `user.phone_number` is therefore only *exact-string*
 * uniqueness. Without the rules below, `9876543210`, `+919876543210`,
 * `+91 98765 43210` and `0919876543210` are four distinct credentials for one
 * physical SIM — four rows that evade each other's rate limits, collision
 * checks and audit trail.
 *
 * So there is exactly one accepted representation, E.164, and the server
 * rejects anything that is not already in it. The client canonicalizes before
 * sending; the server does not silently rewrite, because a rewrite means the
 * value the person confirmed and the value we stored can differ.
 *
 * See `plans/phone-auth-launch-audit.md` AUTH-03.
 */

/**
 * Countries whose numbers may be used as a login credential.
 *
 * India only, at launch. This is not a UX preference — a phone credential drags
 * in per-country DLT/compliance registration, per-country SMS pricing (an
 * unmetered international send is a direct spend attack), and a per-country
 * recovery story for number recycling. Adding a country means answering all
 * three, so the allowlist is explicit rather than "whatever libphonenumber
 * parses".
 */
export const PHONE_CREDENTIAL_COUNTRIES = ["IN"] as const;

export type PhoneCredentialCountry =
  (typeof PHONE_CREDENTIAL_COUNTRIES)[number];

export const PHONE_CREDENTIAL_DEFAULT_COUNTRY: PhoneCredentialCountry = "IN";

export type PhoneCredentialRejection =
  | "empty"
  | "unparseable"
  | "invalid_number"
  | "country_not_supported"
  | "not_a_mobile";

export type PhoneCredentialParseResult =
  | { ok: true; canonical: string; country: PhoneCredentialCountry }
  | { ok: false; reason: PhoneCredentialRejection };

function isSupportedCountry(
  value: string | undefined,
): value is PhoneCredentialCountry {
  return (
    value !== undefined &&
    (PHONE_CREDENTIAL_COUNTRIES as readonly string[]).includes(value)
  );
}

/**
 * Is this an Indian MOBILE number, as opposed to a landline or a service number?
 *
 * `isValid()` alone is not enough. It accepts `+911126234567` (a Delhi landline)
 * and `+912261234567` (a Mumbai one) — both perfectly valid Indian numbers, and
 * neither able to receive an SMS. Registering one as a credential costs a real
 * SMS charge per attempt and produces an account that can never sign in again.
 *
 * The type check would normally come from libphonenumber's `getType()`, but this
 * codebase imports `libphonenumber-js/min`, whose metadata bundle omits type
 * information entirely — `getType()` returns `undefined` for every number. The
 * larger bundle is not worth pulling in for one predicate, so the TRAI rule is
 * encoded directly: an Indian mobile is exactly ten digits beginning 6, 7, 8
 * or 9.
 *
 * This is written against `PHONE_CREDENTIAL_COUNTRIES` being India-only. Adding
 * a country means answering the same question for that country's numbering plan.
 */
function isIndianMobileNumber(nationalNumber: string): boolean {
  return /^[6-9]\d{9}$/.test(nationalNumber);
}

/**
 * Parse free-typed input into a canonical phone credential. Use this on the
 * CLIENT, before submitting — it is what turns "98765 43210" into
 * "+919876543210".
 */
export function parsePhoneCredential(
  input: string,
  defaultCountry: PhoneCredentialCountry = PHONE_CREDENTIAL_DEFAULT_COUNTRY,
): PhoneCredentialParseResult {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, reason: "empty" };

  const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
  if (!parsed) return { ok: false, reason: "unparseable" };
  if (!parsed.isValid()) return { ok: false, reason: "invalid_number" };
  if (!isSupportedCountry(parsed.country)) {
    return { ok: false, reason: "country_not_supported" };
  }
  if (!isIndianMobileNumber(parsed.nationalNumber)) {
    return { ok: false, reason: "not_a_mobile" };
  }

  return { ok: true, canonical: parsed.number, country: parsed.country };
}

/**
 * Server-side acceptance test. Returns true only when `value` is ALREADY the
 * canonical form of a supported number — never a normalized rewrite.
 *
 * A rewriting validator would accept `9876543210` and store `+919876543210`,
 * which sounds helpful right up until the client's own rate-limit key, the
 * collision check and the audit row disagree about which string they are
 * talking about.
 */
export function isCanonicalPhoneCredential(value: string): boolean {
  const parsed = parsePhoneCredential(value);
  return parsed.ok && parsed.canonical === value;
}

/**
 * The digits MSG91 wants: E.164 without the leading `+` (e.g. `919876543210`).
 * Never call this on unvalidated input.
 */
export function toProviderMobile(canonical: string): string {
  return canonical.replace(/^\+/, "");
}

/**
 * Last four digits, for a "code sent to ••••3210" confirmation. Safe to render;
 * the rest of the number is never echoed back to a client that did not already
 * type it.
 */
export function phoneCredentialTail(canonical: string): string {
  return canonical.slice(-4);
}

/** Human-facing rejection copy. Deliberately does not reveal account existence. */
export function phoneCredentialRejectionMessage(
  reason: PhoneCredentialRejection,
): string {
  switch (reason) {
    case "empty":
      return "Enter your mobile number.";
    case "country_not_supported":
      return "Right now we can only text Indian (+91) numbers. Use Google or Apple to continue.";
    case "not_a_mobile":
      return "That looks like a landline. Enter a mobile number we can text.";
    default:
      return "That doesn't look like a valid mobile number.";
  }
}
